import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { APIProvider, Map, Marker, InfoWindow } from "@vis.gl/react-google-maps";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  MapPin,
  Search,
  Phone,
  Globe,
  Star,
  Download,
  Loader2,
  Building2,
  List,
  Map as MapIcon,
  Bookmark,
  Play,
  Trash2,
  Sparkles,
  X,
} from "lucide-react";

const RADII = [
  { v: "0", label: "Any distance" },
  { v: "5", label: "Within 5 km" },
  { v: "10", label: "Within 10 km" },
  { v: "25", label: "Within 25 km" },
  { v: "50", label: "Within 50 km" },
];
const BEACHHEAD = "Windsor, ON";
const SEGMENTS = [
  { label: "Property Mgrs", q: "property management company" },
  { label: "Realtors", q: "real estate agency" },
  { label: "Dental/Medical", q: "dental clinic" },
  { label: "Salons & Spas", q: "hair salon" },
  { label: "Offices", q: "office building" },
  { label: "Daycares", q: "daycare" },
  { label: "Gyms & Studios", q: "gym" },
  { label: "Restaurants", q: "restaurant" },
  { label: "Short-Term Rentals", q: "vacation rental agency" },
];
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function FindLeads() {
  const [configured, setConfigured] = useState(true);
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [radius, setRadius] = useState("10");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [searched, setSearched] = useState(false);
  const [view, setView] = useState("list");
  const [activePin, setActivePin] = useState(null);
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);
  const [segment, setSegment] = useState(null);

  const loadSaved = () =>
    api.get("/places/saved").then((r) => setSaved(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/places/config").then((r) => setConfigured(r.data.configured)).catch(() => {});
    loadSaved();
  }, []);

  const search = async (ov = {}) => {
    const q = ov.query ?? query;
    const loc = ov.location ?? location;
    const rad = ov.radius ?? radius;
    if (!q.trim()) {
      toast.error("Enter what you're looking for, e.g. 'cleaning services'");
      return;
    }
    setLoading(true);
    setSearched(true);
    setSelected({});
    setActivePin(null);
    try {
      const r = await api.post("/places/search", {
        query: q,
        location: loc,
        radius_km: parseFloat(rad),
      });
      setResults(r.data.results || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (pid) => setSelected((s) => ({ ...s, [pid]: !s[pid] }));
  const selectable = results.filter((r) => !r.already_imported && r.place_id);
  const allSelected = selectable.length > 0 && selectable.every((r) => selected[r.place_id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else {
      const s = {};
      selectable.forEach((r) => (s[r.place_id] = true));
      setSelected(s);
    }
  };
  const selectedList = results.filter((r) => selected[r.place_id]);

  const importSelected = async () => {
    if (selectedList.length === 0) {
      toast.error("Select at least one business");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/places/import", {
        places: selectedList,
        extra_tags: segment ? ["b2b-pilot", segment.toLowerCase()] : [],
      });
      toast.success(
        `Imported ${r.data.imported} lead${r.data.imported === 1 ? "" : "s"}` +
          (r.data.skipped ? ` · ${r.data.skipped} already existed` : "")
      );
      setResults((prev) =>
        prev.map((x) => (selected[x.place_id] ? { ...x, already_imported: true } : x))
      );
      setSelected({});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  const saveSearch = async () => {
    if (!query.trim()) {
      toast.error("Run a search first, then save it");
      return;
    }
    setSaving(true);
    try {
      await api.post("/places/saved", { query, location, radius_km: parseFloat(radius) });
      toast.success("Search saved — we'll auto-import new businesses weekly");
      loadSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runSaved = async (s) => {
    toast.message(`Running "${s.name}"…`);
    try {
      const r = await api.post(`/places/saved/${s.id}/run`);
      toast.success(
        `${r.data.imported} new lead${r.data.imported === 1 ? "" : "s"} imported from "${s.name}"`
      );
      loadSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteSaved = async (s) => {
    await api.delete(`/places/saved/${s.id}`);
    toast.success("Saved search removed");
    loadSaved();
  };

  const applySaved = (s) => {
    setQuery(s.query);
    setLocation(s.location || "");
    setRadius(String(s.radius_km || 0));
  };

  const dismissBanner = async () => {
    await Promise.all(
      saved.filter((s) => s.pending_new > 0).map((s) => api.post(`/places/saved/${s.id}/ack`))
    );
    loadSaved();
  };

  const pendingTotal = saved.reduce((n, s) => n + (s.pending_new || 0), 0);
  const coordResults = results.filter((r) => r.lat && r.lng);
  const center = coordResults.length
    ? { lat: coordResults[0].lat, lng: coordResults[0].lng }
    : { lat: 43.6532, lng: -79.3832 };

  return (
    <div className="space-y-6" data-testid="find-leads-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Find Leads</h1>
        <p className="text-muted-foreground mt-1">
          Discover real businesses on Google Maps and import them as leads in one click.
        </p>
      </div>

      {!configured && (
        <div
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300"
          data-testid="places-not-configured"
        >
          Google Maps isn't connected yet. Add a Places API key to start discovering leads.
        </div>
      )}

      {pendingTotal > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 p-4 text-sm"
          data-testid="saved-new-banner"
        >
          <span className="flex items-center gap-2 text-primary">
            <Sparkles className="h-4 w-4 shrink-0" /> {pendingTotal} new prospect
            {pendingTotal === 1 ? "" : "s"} auto-imported from your saved searches (tagged{" "}
            <b>saved-search</b> in Leads).
          </span>
          <Button size="sm" variant="ghost" onClick={dismissBanner} data-testid="dismiss-saved-banner">
            <X className="h-4 w-4 mr-1" /> Dismiss
          </Button>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="grid gap-4 md:grid-cols-[1fr_1fr_180px_auto] md:items-end">
          <div>
            <Label className="text-xs text-muted-foreground">What are you looking for?</Label>
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSegment(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="e.g. cleaning services"
              className="mt-1"
              data-testid="find-leads-query-input"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Location</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="e.g. Toronto, ON"
              className="mt-1"
              data-testid="find-leads-location-input"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Radius</Label>
            <Select value={radius} onValueChange={setRadius}>
              <SelectTrigger className="mt-1" data-testid="find-leads-radius-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {RADII.map((r) => (
                  <SelectItem key={r.v} value={r.v}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => search()}
            disabled={loading || !configured}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="find-leads-search-button"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Search className="h-4 w-4 mr-1.5" /> Search
              </>
            )}
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="segment-presets">
          <span className="text-xs text-muted-foreground self-center mr-1">Quick B2B targets:</span>
          {SEGMENTS.map((sg) => (
            <button
              key={sg.label}
              onClick={() => {
                const loc = location.trim() || BEACHHEAD;
                setSegment(sg.label);
                setQuery(sg.q);
                setLocation(loc);
                search({ query: sg.q, location: loc });
              }}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                segment === sg.label
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-primary/40"
              }`}
              data-testid={`segment-${sg.label}`}
            >
              {sg.label}
            </button>
          ))}
        </div>
        {results.length > 0 && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={saveSearch}
              disabled={saving}
              data-testid="save-search-button"
            >
              <Bookmark className="h-3.5 w-3.5 mr-1.5" /> Save this search
            </Button>
          </div>
        )}
      </div>

      {saved.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="saved-searches-panel">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Bookmark className="h-4 w-4 text-primary" /> Saved searches
            <span className="text-xs font-normal text-muted-foreground">
              · auto-imports new businesses weekly
            </span>
          </h2>
          <div className="space-y-2">
            {saved.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-secondary/40 px-3 py-2"
                data-testid={`saved-search-item-${s.id}`}
              >
                <button onClick={() => applySaved(s)} className="text-left flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{s.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.last_run_at
                      ? `Last run ${new Date(s.last_run_at).toLocaleDateString()} · ${
                          s.total_imported || 0
                        } imported total`
                      : "Not run yet"}
                  </p>
                </button>
                {s.pending_new > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary shrink-0">
                    {s.pending_new} new
                  </span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8"
                  onClick={() => runSaved(s)}
                  data-testid={`run-saved-${s.id}`}
                >
                  <Play className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-rose-400"
                  onClick={() => deleteSaved(s)}
                  data-testid={`delete-saved-${s.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {results.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer"
              data-testid="find-leads-select-all"
            >
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="h-4 w-4 cursor-pointer"
                disabled={selectable.length === 0}
              />
              Select all new ({selectable.length})
            </label>
            <div className="flex rounded-lg border border-border overflow-hidden">
              <button
                onClick={() => setView("list")}
                className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${
                  view === "list"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
                data-testid="view-toggle-list"
              >
                <List className="h-3.5 w-3.5" /> List
              </button>
              <button
                onClick={() => setView("map")}
                className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${
                  view === "map"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
                data-testid="view-toggle-map"
              >
                <MapIcon className="h-3.5 w-3.5" /> Map
              </button>
            </div>
          </div>
          <Button
            onClick={importSelected}
            disabled={importing || selectedList.length === 0}
            data-testid="find-leads-import-button"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {importing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            ) : (
              <Download className="h-4 w-4 mr-1.5" />
            )}
            Import {selectedList.length > 0 ? selectedList.length : ""} selected
          </Button>
        </div>
      )}

      {results.length > 0 && view === "map" && (
        <div
          className="rounded-2xl border border-border overflow-hidden"
          style={{ height: 520 }}
          data-testid="find-leads-map"
        >
          {MAPS_KEY ? (
            <APIProvider apiKey={MAPS_KEY}>
              <Map
                key={`${center.lat}-${center.lng}`}
                defaultCenter={center}
                defaultZoom={11}
                style={{ width: "100%", height: "100%" }}
                gestureHandling="greedy"
                clickableIcons={false}
                disableDefaultUI={false}
              >
                {coordResults.map((r) => (
                  <Marker
                    key={r.place_id}
                    position={{ lat: r.lat, lng: r.lng }}
                    title={r.name}
                    onClick={() => setActivePin(r)}
                  />
                ))}
                {activePin && (
                  <InfoWindow
                    position={{ lat: activePin.lat, lng: activePin.lng }}
                    onCloseClick={() => setActivePin(null)}
                  >
                    <div style={{ maxWidth: 220, color: "#111" }}>
                      <p style={{ fontWeight: 700, margin: 0 }}>{activePin.name}</p>
                      {activePin.category && (
                        <p style={{ margin: "2px 0", fontSize: 12, textTransform: "capitalize" }}>
                          {activePin.category}
                        </p>
                      )}
                      {activePin.phone && (
                        <p style={{ margin: "2px 0", fontSize: 12 }}>{activePin.phone}</p>
                      )}
                      {activePin.address && (
                        <p style={{ margin: "2px 0", fontSize: 11, color: "#555" }}>
                          {activePin.address}
                        </p>
                      )}
                      {!activePin.already_imported && activePin.place_id && (
                        <button
                          onClick={() => toggle(activePin.place_id)}
                          style={{
                            marginTop: 6,
                            fontSize: 12,
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: "1px solid #ccc",
                            cursor: "pointer",
                            background: selected[activePin.place_id] ? "#e0f2fe" : "#fff",
                          }}
                        >
                          {selected[activePin.place_id] ? "✓ Selected" : "Select"}
                        </button>
                      )}
                      {activePin.already_imported && (
                        <p style={{ margin: "4px 0 0", fontSize: 11, color: "#059669" }}>
                          Already imported
                        </p>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </Map>
            </APIProvider>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Map key not configured.
            </div>
          )}
        </div>
      )}

      {view === "list" && (
        <div className="space-y-3">
          {searched && !loading && results.length === 0 && (
            <div
              className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground"
              data-testid="find-leads-empty"
            >
              No businesses found. Try a broader query or a larger radius.
            </div>
          )}
          {results.map((r) => (
            <div
              key={r.place_id || r.name}
              className={`rounded-xl border p-4 transition-colors ${
                r.already_imported
                  ? "border-border/50 bg-card/50 opacity-70"
                  : selected[r.place_id]
                  ? "border-primary/50 bg-primary/5"
                  : "border-border bg-card hover:border-primary/30"
              }`}
              data-testid={`result-row-${r.place_id}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={!!selected[r.place_id]}
                  onChange={() => toggle(r.place_id)}
                  disabled={r.already_imported}
                  className="mt-1 h-4 w-4 cursor-pointer shrink-0"
                  data-testid={`result-checkbox-${r.place_id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-semibold">{r.name}</span>
                    {r.rating != null && (
                      <span className="flex items-center gap-1 text-xs text-amber-400">
                        <Star className="h-3 w-3 fill-amber-400" /> {r.rating} ({r.reviews || 0})
                      </span>
                    )}
                    {r.already_imported && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                        Already imported
                      </span>
                    )}
                  </div>
                  {r.category && (
                    <p className="text-xs text-muted-foreground mt-1 capitalize">{r.category}</p>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                    {r.phone && (
                      <span className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" /> {r.phone}
                      </span>
                    )}
                    {r.website && (
                      <a
                        href={r.website}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 text-primary hover:underline"
                      >
                        <Globe className="h-3.5 w-3.5" /> Website
                      </a>
                    )}
                  </div>
                  {r.address && (
                    <p className="flex items-start gap-1.5 mt-1 text-xs text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {r.address}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
